import { useState, useMemo, useCallback, useRef } from 'react';
import { useInfiniteQuery, InfiniteData } from '@tanstack/react-query';
import { api, ConversationsPageResponse } from '@/services/api';
import { Conversation } from '@/types';
import { queryClient } from './queryClient';

export interface UseConversationsOptions {
  userId?: string | null;
  lojaId?: string | null;
  tenantId?: string | null;
  connectionId?: string | null;
  archived?: boolean;
  transbordo?: boolean;
  myOnly?: boolean;
  q?: string;
  signal?: AbortSignal;
}

export function buildConversationsQueryKey(options?: UseConversationsOptions) {
  return [
    'conversations',
    {
      userId: options?.userId || null,
      lojaId: options?.lojaId || null,
      tenantId: options?.tenantId || null,
      connectionId: options?.connectionId || null,
      archived: options?.archived ?? false,
      transbordo: Boolean(options?.transbordo),
      myOnly: Boolean(options?.myOnly),
      q: options?.q || ''
    }
  ] as const;
}

// Comparador determinístico de timestamps descendente com microssegundos (ISO 8601, NULLS LAST)
export function compareConversationTimestampsDesc(dateA?: string | null, dateB?: string | null): number {
  if (dateA === dateB) return 0;
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1; // NULLS LAST
  if (!dateB) return -1; // NULLS LAST

  // Fast-path para strings ISO 8601 canônicas idênticas em timezone UTC (Z ou offset fixo)
  // Strings ISO 8601 são lexicograficamente ordenáveis sem necessidade de instanciar Date
  const isIsoA = dateA.length >= 19 && (dateA.endsWith('Z') || dateA.includes('+') || dateA.includes('-'));
  const isIsoB = dateB.length >= 19 && (dateB.endsWith('Z') || dateB.includes('+') || dateB.includes('-'));

  if (isIsoA && isIsoB && dateA.length === dateB.length) {
    if (dateB > dateA) return 1;
    if (dateB < dateA) return -1;
    return 0;
  }

  const timeA = new Date(dateA).getTime();
  const timeB = new Date(dateB).getTime();

  if (isNaN(timeA) && isNaN(timeB)) {
    return String(dateB).localeCompare(String(dateA)); // DESC
  }
  if (isNaN(timeA)) return 1;
  if (isNaN(timeB)) return -1;

  if (timeA !== timeB) {
    return timeB - timeA; // DESC
  }

  // Desempate por fração de segundos (microssegundos/nanossegundos)
  const matchA = dateA.match(/\.(\d+)/);
  const matchB = dateB.match(/\.(\d+)/);

  if (matchA || matchB) {
    const fracA = (matchA ? matchA[1] : '').padEnd(9, '0').slice(0, 9);
    const fracB = (matchB ? matchB[1] : '').padEnd(9, '0').slice(0, 9);
    if (fracA !== fracB) {
      return fracB.localeCompare(fracA); // DESC
    }
  }

  return 0;
}

// Comparador determinístico de conversas: last_message_at DESC NULLS LAST, id DESC
export function compareConversations(a: Conversation, b: Conversation): number {
  const timeDiff = compareConversationTimestampsDesc(a.last_message_at, b.last_message_at);
  if (timeDiff !== 0) {
    return timeDiff;
  }
  return (b.id || '').localeCompare(a.id || ''); // DESC tie-breaker por ID
}

export interface ConversationMutationRecord {
  id: string;
  type: 'insert' | 'update' | 'delete' | 'rollback_delete';
  seq: number;
  data?: any;
}

// ============================================================================
// Registro de Lifecycle, Geração e Revogação por Contexto
// ============================================================================
export interface ConversationContextLifecycle {
  generation: number;
  isRevoked: boolean;
  status?: number;
  message?: string;
}

let globalGenerationCounter = 1;
let globalRecoveryCounter = 0;
const contextLifecycleMap = new Map<string, ConversationContextLifecycle>();
const activeRecoveryByKey = new Map<string, number>();

// Mutações e reconciliação temporal
const mutationSeqByKey = new Map<string, number>();
const mutationsByKey = new Map<string, Map<string, ConversationMutationRecord>>();
const tombstonesByKey = new Map<string, Set<string>>();
const activeFetchesByKey = new Map<string, Map<number, number>>();
let globalFetchIdCounter = 0;

export function getContextLifecycle(storageKey: string): ConversationContextLifecycle {
  let ctx = contextLifecycleMap.get(storageKey);
  if (!ctx) {
    ctx = { generation: ++globalGenerationCounter, isRevoked: false };
    contextLifecycleMap.set(storageKey, ctx);
  }
  return ctx;
}

export function isContextRevoked(storageKey: string): boolean {
  const ctx = contextLifecycleMap.get(storageKey);
  return Boolean(ctx?.isRevoked);
}

export function isOperationValid(storageKey: string, expectedGeneration?: number): boolean {
  const ctx = contextLifecycleMap.get(storageKey);
  if (!ctx) return false;
  if (ctx.isRevoked) return false;
  if (expectedGeneration !== undefined && ctx.generation !== expectedGeneration) return false;
  return true;
}

export function isCallbackActive(storageKey: string, callbackGeneration: number): boolean {
  return isOperationValid(storageKey, callbackGeneration);
}

export function revokeContext(
  storageKey: string,
  status: number,
  message: string
): ConversationContextLifecycle {
  let ctx = contextLifecycleMap.get(storageKey);
  if (!ctx) {
    ctx = { generation: ++globalGenerationCounter, isRevoked: true, status, message };
    contextLifecycleMap.set(storageKey, ctx);
  } else {
    ctx.isRevoked = true;
    ctx.status = status;
    ctx.message = message;
    ctx.generation = ++globalGenerationCounter;
  }

  clearConversationsTracking(storageKey);
  return ctx;
}

export function resetContextAuthorization(storageKey: string): ConversationContextLifecycle {
  const ctx: ConversationContextLifecycle = {
    generation: ++globalGenerationCounter,
    isRevoked: false,
    status: undefined,
    message: undefined
  };
  contextLifecycleMap.set(storageKey, ctx);
  return ctx;
}

export function clearConversationsTracking(storageKeyOrAll?: string) {
  if (storageKeyOrAll) {
    for (const key of Array.from(mutationsByKey.keys())) {
      if (key.includes(storageKeyOrAll) || (storageKeyOrAll.startsWith('[') && key === storageKeyOrAll)) {
        mutationsByKey.delete(key);
        mutationSeqByKey.delete(key);
        activeFetchesByKey.delete(key);
        tombstonesByKey.delete(key);
        activeRecoveryByKey.delete(key);
      }
    }
  } else {
    for (const ctx of contextLifecycleMap.values()) {
      ctx.isRevoked = true;
      ctx.generation = ++globalGenerationCounter;
    }
    mutationsByKey.clear();
    mutationSeqByKey.clear();
    activeFetchesByKey.clear();
    tombstonesByKey.clear();
    activeRecoveryByKey.clear();
    globalGenerationCounter += 1000;
  }
}

export function clearConversationsCache(customQueryClient = queryClient) {
  contextLifecycleMap.clear();
  mutationsByKey.clear();
  mutationSeqByKey.clear();
  activeFetchesByKey.clear();
  tombstonesByKey.clear();
  activeRecoveryByKey.clear();
  globalGenerationCounter += 1000;
  customQueryClient.removeQueries({ queryKey: ['conversations'] });
}

export function recordConversationMutation(
  storageKey: string,
  id: string,
  type: 'insert' | 'update' | 'delete' | 'rollback_delete',
  data?: any
): number {
  if (isContextRevoked(storageKey)) {
    return -1;
  }

  const currentSeq = (mutationSeqByKey.get(storageKey) || 0) + 1;
  mutationSeqByKey.set(storageKey, currentSeq);

  let map = mutationsByKey.get(storageKey);
  if (!map) {
    map = new Map<string, ConversationMutationRecord>();
    mutationsByKey.set(storageKey, map);
  }

  let tombstones = tombstonesByKey.get(storageKey);
  if (!tombstones) {
    tombstones = new Set<string>();
    tombstonesByKey.set(storageKey, tombstones);
  }

  if (type === 'delete') {
    map.set(id, { id, type: 'delete', seq: currentSeq, data: null });
    tombstones.add(id);
  } else if (type === 'rollback_delete') {
    tombstones.delete(id);
    if (data) {
      map.set(id, { id, type: 'insert', seq: currentSeq, data });
    } else {
      map.delete(id);
    }
  } else if (type === 'update') {
    if (tombstones.has(id)) {
      return currentSeq; // Não ressuscita deletado
    }
    const existing = map.get(id);
    if (existing && existing.type === 'delete') {
      return currentSeq;
    }
    if (existing && existing.type === 'insert') {
      const merged = { ...existing.data, ...data };
      map.set(id, { id, type: 'insert', seq: currentSeq, data: merged });
    } else if (existing && existing.type === 'update') {
      const merged = { ...existing.data, ...data };
      map.set(id, { id, type: 'update', seq: currentSeq, data: merged });
    } else {
      map.set(id, { id, type: 'update', seq: currentSeq, data });
    }
  } else if (type === 'insert') {
    if (tombstones.has(id)) {
      // DELETE confirmado impede que INSERT atrasado ressuscite conversa
      return currentSeq;
    }
    const existing = map.get(id);
    if (existing && existing.type === 'delete') {
      return currentSeq;
    }
    map.set(id, { id, type: 'insert', seq: currentSeq, data });
  }

  return currentSeq;
}

export function startFetchTracking(storageKey: string): { fetchId: number; startSeq: number } {
  const fetchId = ++globalFetchIdCounter;
  const startSeq = mutationSeqByKey.get(storageKey) || 0;

  let activeMap = activeFetchesByKey.get(storageKey);
  if (!activeMap) {
    activeMap = new Map<number, number>();
    activeFetchesByKey.set(storageKey, activeMap);
  }
  activeMap.set(fetchId, startSeq);

  return { fetchId, startSeq };
}

export function finishFetchTracking(storageKey: string, fetchId: number, startSeq: number) {
  const activeMap = activeFetchesByKey.get(storageKey);
  if (activeMap) {
    activeMap.delete(fetchId);
    if (activeMap.size === 0) {
      activeFetchesByKey.delete(storageKey);
      const map = mutationsByKey.get(storageKey);
      if (map) {
        for (const [id, mut] of Array.from(map.entries())) {
          if (mut.seq <= startSeq && mut.type !== 'delete') {
            map.delete(id);
          }
        }
      }
    }
  }
}

export function reconcileFetchedItems(
  storageKey: string,
  fetchedItems: Conversation[],
  startSeq: number,
  includeLocalInserts = true
): Conversation[] {
  const map = mutationsByKey.get(storageKey);
  const tombstones = tombstonesByKey.get(storageKey);

  const resultItems: Conversation[] = [];
  const handledIds = new Set<string>();

  for (const item of fetchedItems) {
    if (!item || !item.id) continue;
    if (tombstones && tombstones.has(item.id)) continue;

    if (map) {
      const mut = map.get(item.id);
      if (mut) {
        if (mut.type === 'delete') continue;
        if (mut.type === 'update') {
          resultItems.push({ ...item, ...mut.data });
          handledIds.add(item.id);
          continue;
        }
      }
    }

    resultItems.push(item);
    handledIds.add(item.id);
  }

  // Inclui inserts locais registrados durante o fetch
  if (map && includeLocalInserts) {
    for (const mut of map.values()) {
      if (mut.type === 'insert' && mut.seq > startSeq && !handledIds.has(mut.id) && mut.data) {
        if (tombstones && tombstones.has(mut.id)) continue;
        resultItems.push(mut.data);
        handledIds.add(mut.id);
      }
    }
  }

  return resultItems.filter(c => belongsToConversationsQuery(storageKey, c));
}

// Consolidador das páginas do TanStack Query + mutações locais ativas (SEM cache paralelo)
export function getConsolidatedConversations(
  storageKey: string,
  pages?: { items: Conversation[] }[]
): Conversation[] {
  if (isContextRevoked(storageKey)) return [];

  const map = mutationsByKey.get(storageKey);
  const tombstones = tombstonesByKey.get(storageKey);
  const activeMutations = map ? new Map(map) : null;
  const convMap = new Map<string, Conversation>();

  if (pages) {
    for (const page of pages) {
      if (page?.items) {
        for (const conv of page.items) {
          if (!conv || !conv.id) continue;
          if (tombstones && tombstones.has(conv.id)) continue;
          if (activeMutations) {
            const mut = activeMutations.get(conv.id);
            if (mut) {
              if (mut.type === 'delete') continue;
              if (mut.type === 'update') {
                convMap.set(conv.id, { ...conv, ...mut.data });
                continue;
              }
            }
          }
          convMap.set(conv.id, conv);
        }
      }
    }
  }

  if (activeMutations) {
    for (const mut of activeMutations.values()) {
      if ((mut.type === 'insert' || mut.type === 'update') && mut.data?.id && !convMap.has(mut.id)) {
        if (tombstones && tombstones.has(mut.id)) continue;
        convMap.set(mut.id, mut.data);
      }
    }
  }

  const list = Array.from(convMap.values()).filter(c => belongsToConversationsQuery(storageKey, c));
  list.sort(compareConversations);
  return list;
}

/** Membership is independent of DELETE: archive/unarchive must remain reversible. */
export function belongsToConversationsQuery(storageKey: string, conv: Conversation): boolean {
  let scope: any;
  try { scope = JSON.parse(storageKey)?.[1]; } catch { return true; }
  if (!scope || typeof scope !== 'object') return true;
  if (Boolean(conv.is_archived) !== Boolean(scope.archived)) return false;
  for (const [key, field] of [['lojaId', 'loja_id'], ['tenantId', 'tenant_id'], ['connectionId', 'connection_id']]) {
    if (scope[key] && (conv as any)[field] && scope[key] !== (conv as any)[field]) return false;
  }
  return true;
}

/** Operation-local rollback snapshots, bound to the exact admitted query and generation. */
export function captureConversationArchiveRollback(id: string) {
  const snapshots = queryClient.getQueryCache().findAll({ queryKey: ['conversations'] }).flatMap(query => {
    const storageKey = getStorageKey(query.queryKey);
    if (isContextRevoked(storageKey)) return [];
    const generation = getContextLifecycle(storageKey).generation;
    const cached = query.state.data as InfiniteData<ConversationsPageResponse> | undefined;
    return (cached?.pages.flatMap(page => page.items) || [])
      .filter(row => row.id === id || row.lead_id === id)
      // Retain the existing journal while PATCH can still roll back. Reuse its
      // in-flight retention barrier, not a second cache of conversation data.
      .map(row => ({ query, storageKey, generation, row, retention: startFetchTracking(storageKey) }));
  });
  const restore = () => {
    for (const { query, storageKey, generation, row } of snapshots) {
      if (!isOperationValid(storageKey, generation)
        || queryClient.getQueryCache().find({ queryKey: query.queryKey, exact: true }) !== query) continue;
      // Restore only the archive field, not newer preview/unread/status updates.
      updateScopedConversationCache(query.queryKey, row.id, { is_archived: row.is_archived }, row);
    }
  };
  return Object.assign(restore, {
    release: () => {
      for (const { storageKey, generation, retention } of snapshots) {
        // Do not clean a replacement authorization generation's journal.
        finishFetchTracking(storageKey, retention.fetchId,
          isOperationValid(storageKey, generation) ? retention.startSeq : -1);
      }
      snapshots.length = 0;
    }
  });
}

/** Lead apagado: remove as conversas dele de todos os filtros, com tombstone contra refetch/realtime tardio. */
export function removeLeadConversationsFromCache(leadId: string) {
  for (const query of queryClient.getQueryCache().findAll({ queryKey: ['conversations'] })) {
    const storageKey = getStorageKey(query.queryKey);
    const cached = query.state.data as InfiniteData<ConversationsPageResponse> | undefined;
    const journal = Array.from(mutationsByKey.get(storageKey)?.values() || []).map(m => m.data);
    const ids = [...(cached?.pages.flatMap(p => p.items) || []), ...journal]
      .filter(c => c?.id && c.lead_id === leadId).map(c => c.id);
    for (const id of ids) recordConversationMutation(storageKey, id, 'delete');
    if (cached && ids.length) {
      queryClient.setQueryData(query.queryKey, { ...cached, pages: cached.pages.map(p => ({ ...p, items: p.items.filter(c => c.lead_id !== leadId) })) });
    }
  }
}

/** Update only a previously admitted row; never synthesize membership in another filter. */
export function updateScopedConversationCache(queryKey: readonly any[], id: string, updates: Partial<Conversation>, admittedSnapshot?: Conversation) {
  const storageKey = getStorageKey(queryKey);
  if (isContextRevoked(storageKey) || tombstonesByKey.get(storageKey)?.has(id)) return;
  const old = queryClient.getQueryData<InfiniteData<ConversationsPageResponse>>(queryKey);
  const previous = old?.pages.flatMap(p => p.items).find(c => c.id === id)
    || mutationsByKey.get(storageKey)?.get(id)?.data || admittedSnapshot;
  if (!previous?.id) return;
  const merged = { ...previous, ...updates, id, is_archived: updates.is_archived ?? previous.is_archived };
  recordConversationMutation(storageKey, id, 'update', merged);
  if (!old) return;
  let found = false;
  const pages = old.pages.map(p => ({ ...p, items: p.items.flatMap(c => {
    if (c.id !== id) return [c];
    found = true;
    const next = { ...c, ...updates, is_archived: updates.is_archived ?? c.is_archived };
    return belongsToConversationsQuery(storageKey, next) ? [next] : [];
  }) }));
  // A retained admitted snapshot allows rollback/unarchive, not arbitrary INSERT.
  if (!found && previous?.id && belongsToConversationsQuery(storageKey, merged as Conversation) && pages.length) {
    pages[0] = { ...pages[0], items: [merged as Conversation, ...pages[0].items] };
  }
  queryClient.setQueryData(queryKey, { ...old, pages });
}

function getStorageKey(key: readonly any[]): string {
  return JSON.stringify(key);
}

/**
 * Recuperação autoritativa: cobre do topo até a fronteira histórica previamente carregada.
 * NUNCA encolhe a lista em refreshes; aborta sem publicar resultado parcial em caso de erro intermediário.
 */
export async function recoverConversations(
  options?: UseConversationsOptions,
  customQueryClient = queryClient,
  callerGeneration?: number
): Promise<InfiniteData<ConversationsPageResponse> | undefined> {
  const queryKey = buildConversationsQueryKey(options);
  const storageKey = getStorageKey(queryKey);

  const initialCtx = getContextLifecycle(storageKey);
  const opGen = callerGeneration ?? initialCtx.generation;

  if (initialCtx.isRevoked || !isOperationValid(storageKey, opGen)) {
    return undefined;
  }

  const recoveryId = ++globalRecoveryCounter;
  activeRecoveryByKey.set(storageKey, recoveryId);

  const currentCached = customQueryClient.getQueryData<InfiniteData<ConversationsPageResponse>>(queryKey);
  let oldestKnownConv: Conversation | null = null;
  let totalCachedCount = 0;

  if (currentCached?.pages && currentCached.pages.length > 0) {
    for (const page of currentCached.pages) {
      if (page?.items && page.items.length > 0) {
        totalCachedCount += page.items.length;
        for (const item of page.items) {
          if (item?.id) {
            if (!oldestKnownConv || compareConversations(item, oldestKnownConv) > 0) {
              oldestKnownConv = item;
            }
          }
        }
      }
    }
  }

  const oldestKnownId = oldestKnownConv?.id || null;

  const { fetchId, startSeq } = startFetchTracking(storageKey);
  const recoveredPages: ConversationsPageResponse[] = [];
  const recoveredPageParams: (string | null)[] = [null];
  let nextCursor: string | null = null;
  let hasMore = true;
  let coveredOldest = false;

  try {
    const cachedParams = currentCached?.pageParams as (string | null)[] | undefined;
    if (cachedParams && cachedParams.length > 1) {
      // Revalidação em PARALELO para páginas já conhecidas em cache
      const fetchPromises = cachedParams.map(cursor =>
        api.getConversationsPage({
          cursor: cursor || undefined,
          limit: 50,
          loja_id: options?.lojaId,
          tenant_id: options?.tenantId,
          connection_id: options?.connectionId,
          archived: options?.archived,
          transbordo: options?.transbordo,
          my_only: options?.myOnly,
          q: options?.q,
          signal: options?.signal
        })
      );

      let parallelPages: ConversationsPageResponse[];
      try {
        parallelPages = await Promise.all(fetchPromises);
      } catch (err) {
        finishFetchTracking(storageKey, fetchId, startSeq);
        throw err;
      }

      if (!isOperationValid(storageKey, opGen) || activeRecoveryByKey.get(storageKey) !== recoveryId) {
        finishFetchTracking(storageKey, fetchId, startSeq);
        return undefined;
      }

      let isParallelValid = true;
      for (let i = 0; i < parallelPages.length - 1; i++) {
        const pageI = parallelPages[i];
        const pageIPlus1 = parallelPages[i + 1];

        if (!pageI.items || pageI.items.length === 0 || !pageIPlus1.items || pageIPlus1.items.length === 0) {
          isParallelValid = false;
          break;
        }

        if (!pageI.hasMore || !pageI.nextCursor || pageI.nextCursor !== cachedParams[i + 1]) {
          isParallelValid = false;
          break;
        }

        const lastItemOfI = pageI.items[pageI.items.length - 1];
        const firstItemOfIPlus1 = pageIPlus1.items[0];

        if (compareConversations(lastItemOfI, firstItemOfIPlus1) >= 0) {
          isParallelValid = false;
          break;
        }
      }

      if (isParallelValid) {
        for (let i = 0; i < parallelPages.length; i++) {
          const pageRes = parallelPages[i];
          recoveredPages.push(pageRes);
          if (i > 0) {
            recoveredPageParams.push(cachedParams[i] ?? null);
          }

          if (oldestKnownId && pageRes.items?.some(c => c.id === oldestKnownId)) {
            coveredOldest = true;
          }
          if (oldestKnownConv && !coveredOldest && pageRes.items && pageRes.items.length > 0) {
            const lastInPage = pageRes.items[pageRes.items.length - 1];
            if (compareConversations(lastInPage, oldestKnownConv) >= 0) {
              coveredOldest = true;
            }
          }
        }

        const lastParallelPage = parallelPages[parallelPages.length - 1];
        if (coveredOldest || !lastParallelPage.hasMore || !lastParallelPage.nextCursor) {
          hasMore = false;
        } else {
          nextCursor = lastParallelPage.nextCursor;
          recoveredPageParams.push(nextCursor);
        }
      }
    }

    while (hasMore) {
      if (!isOperationValid(storageKey, opGen) || activeRecoveryByKey.get(storageKey) !== recoveryId) {
        finishFetchTracking(storageKey, fetchId, startSeq);
        return undefined;
      }

      const pageRes = await api.getConversationsPage({
        cursor: nextCursor,
        limit: 50,
        loja_id: options?.lojaId,
        tenant_id: options?.tenantId,
        connection_id: options?.connectionId,
        archived: options?.archived,
        transbordo: options?.transbordo,
        my_only: options?.myOnly,
        q: options?.q,
        signal: options?.signal
      });

      if (!isOperationValid(storageKey, opGen) || activeRecoveryByKey.get(storageKey) !== recoveryId) {
        finishFetchTracking(storageKey, fetchId, startSeq);
        return undefined;
      }

      recoveredPages.push(pageRes);

      // Verificação 1: o ID mais antigo conhecido foi coberto diretamente
      if (oldestKnownId && pageRes.items?.some(c => c.id === oldestKnownId)) {
        coveredOldest = true;
      }

      // Verificação 2: se o ID mais antigo foi excluído no servidor, ordenação temporal confirma que passamos por ele
      if (oldestKnownConv && !coveredOldest && pageRes.items && pageRes.items.length > 0) {
        const lastInPage = pageRes.items[pageRes.items.length - 1];
        if (compareConversations(lastInPage, oldestKnownConv) >= 0) {
          coveredOldest = true;
        }
      }

      if (coveredOldest || !pageRes.hasMore || !pageRes.nextCursor) {
        hasMore = false;
        break;
      }

      recoveredPageParams.push(pageRes.nextCursor);
      nextCursor = pageRes.nextCursor;
    }

    if (!isOperationValid(storageKey, opGen) || activeRecoveryByKey.get(storageKey) !== recoveryId) {
      finishFetchTracking(storageKey, fetchId, startSeq);
      return undefined;
    }

    // Salvaguarda: se havia itens em cache e a recuperação parou antes de cobrir a fronteira com hasMore=true, rejeita
    if (totalCachedCount > 0 && !coveredOldest && hasMore) {
      finishFetchTracking(storageKey, fetchId, startSeq);
      return undefined;
    }

    // Reconcile membership on every page; append local inserts only once.
    for (let i = 0; i < recoveredPages.length; i++) {
      recoveredPages[i] = { ...recoveredPages[i], items: reconcileFetchedItems(storageKey, recoveredPages[i].items, startSeq, i === recoveredPages.length - 1) };
    }

    const finalData: InfiniteData<ConversationsPageResponse> = {
      pages: recoveredPages,
      pageParams: recoveredPageParams
    };

    customQueryClient.setQueryData<InfiniteData<ConversationsPageResponse>>(queryKey, finalData);
    finishFetchTracking(storageKey, fetchId, startSeq);
    return finalData;
  } catch (err) {
    finishFetchTracking(storageKey, fetchId, startSeq);
    throw err;
  }
}

/**
 * Hook TanStack Query v5 para Lista de Conversas da Inbox com paginação por cursor,
 * revalidação autoritativa contínua e reconciliação em tempo real.
 */
export function useConversations(options?: UseConversationsOptions) {
  const queryKey = useMemo(() => buildConversationsQueryKey(options), [
    options?.userId,
    options?.lojaId,
    options?.tenantId,
    options?.connectionId,
    options?.archived,
    options?.transbordo,
    options?.myOnly,
    options?.q
  ]);
  const storageKey = useMemo(() => getStorageKey(queryKey), [queryKey]);

  const currentLifecycle = getContextLifecycle(storageKey);
  const myGeneration = currentLifecycle.generation;

  // Authorization failures belong to the request scope, never to the hook instance.
  const activeScope = useRef(storageKey);
  activeScope.current = storageKey;
  const [scopedSyncError, setScopedSyncError] = useState<{ key: string; error: Error } | null>(null);
  const [scopedAccessError, setScopedAccessError] = useState<{ key: string; status: number; message: string } | null>(null);
  const syncError = scopedSyncError?.key === storageKey ? scopedSyncError.error : null;
  const accessDeniedError = scopedAccessError?.key === storageKey ? scopedAccessError : null;
  const setSyncError = (error: Error | null) => { if (activeScope.current === storageKey) setScopedSyncError(error ? { key: storageKey, error } : null); };
  const setAccessDeniedError = (error: { status: number; message: string }) => { if (activeScope.current === storageKey) setScopedAccessError({ key: storageKey, ...error }); };
  const [, setMutationTick] = useState(0);

  const isRevoked = isContextRevoked(storageKey);
  const effectiveAccessDenied = useMemo(() => {
    if (accessDeniedError) return accessDeniedError;
    if (isRevoked) {
      return {
        status: currentLifecycle.status || 403,
        message: currentLifecycle.message || (currentLifecycle.status === 401 ? 'Não autorizado' : 'Acesso negado')
      };
    }
    return null;
  }, [accessDeniedError, isRevoked, currentLifecycle.status, currentLifecycle.message]);

  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      if (!isOperationValid(storageKey, myGeneration) || effectiveAccessDenied) {
        return { items: [], nextCursor: null, hasMore: false };
      }
      try {
        const { fetchId, startSeq } = startFetchTracking(storageKey);
        try {
        const page = await api.getConversationsPage({
          cursor: pageParam as string | null,
          limit: 50,
          loja_id: options?.lojaId,
          tenant_id: options?.tenantId,
          connection_id: options?.connectionId,
          archived: options?.archived,
          transbordo: options?.transbordo,
          my_only: options?.myOnly,
          q: options?.q,
          signal: signal || options?.signal
        });
        if (!isOperationValid(storageKey, myGeneration)) return { items: [], nextCursor: null, hasMore: false };
        return { ...page, items: reconcileFetchedItems(storageKey, page.items, startSeq) };
        } finally { finishFetchTracking(storageKey, fetchId, startSeq); }
      } catch (err: any) {
        if (err?.status === 401 || err?.status === 403) {
          setAccessDeniedError({ status: err.status, message: err.message });
          revokeContext(storageKey, err.status, err.message);
          queryClient.setQueryData(queryKey, { pages: [], pageParams: [] });
        }
        throw err;
      }
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage?.nextCursor || undefined,
    enabled: !isContextRevoked(storageKey) && !effectiveAccessDenied,
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) return false;
      return failureCount < 2;
    }
  });

  const conversations = useMemo(() => {
    if (effectiveAccessDenied || isRevoked) return [];
    return getConsolidatedConversations(storageKey, query.data?.pages);
  }, [storageKey, query.data, effectiveAccessDenied, isRevoked]);

  // Callback de refetch referencialmente estável
  const refetch = useCallback(async () => {
    if (!isCallbackActive(storageKey, myGeneration)) {
      return { data: undefined, isError: false, error: null };
    }
    try {
      const recovered = await recoverConversations(
        {
          userId: options?.userId,
          lojaId: options?.lojaId,
          tenantId: options?.tenantId,
          connectionId: options?.connectionId,
          archived: options?.archived,
          transbordo: options?.transbordo,
          myOnly: options?.myOnly,
          q: options?.q
        },
        queryClient,
        myGeneration
      );
      if (isCallbackActive(storageKey, myGeneration)) {
        setSyncError(null);
      }
      return { data: recovered, isError: false, error: null };
    } catch (err: any) {
      if (err?.status === 401 || err?.status === 403) {
        setAccessDeniedError({ status: err.status, message: err.message });
        revokeContext(storageKey, err.status, err.message);
        queryClient.setQueryData(queryKey, { pages: [], pageParams: [] });
        return { data: undefined, isError: true, error: err };
      }
      if (isCallbackActive(storageKey, myGeneration)) {
        setSyncError(err);
      }
      return { data: undefined, isError: true, error: err };
    }
  }, [
    storageKey,
    myGeneration,
    queryKey,
    options?.userId,
    options?.lojaId,
    options?.tenantId,
    options?.connectionId,
    options?.archived,
    options?.transbordo,
    options?.myOnly,
    options?.q
  ]);

  const loadMore = useCallback(async () => {
    if (!isCallbackActive(storageKey, myGeneration)) return;
    if (query.hasNextPage && !query.isFetchingNextPage) {
      await query.fetchNextPage();
    }
  }, [storageKey, myGeneration, query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  const addRealtimeConversation = useCallback((conv: Conversation) => {
    if (!isCallbackActive(storageKey, myGeneration)) return;
    if (conv.is_archived === undefined) {
      updateScopedConversationCache(queryKey, conv.id, conv);
      void refetch();
      return;
    }
    if (!belongsToConversationsQuery(storageKey, conv)) {
      updateScopedConversationCache(queryKey, conv.id, conv);
      return;
    }
    // Restricted server predicates must be admitted by the server, not guessed locally.
    const scope = queryKey[1];
    if (scope.myOnly || scope.transbordo || scope.q) {
      updateScopedConversationCache(queryKey, conv.id, conv);
      void refetch();
      return;
    }
    if ((scope.lojaId && conv.loja_id !== scope.lojaId) ||
        (scope.tenantId && conv.tenant_id !== scope.tenantId) ||
        (scope.connectionId && (conv as Conversation & { connection_id?: string | null }).connection_id !== scope.connectionId)) return;
    const currentTombstones = tombstonesByKey.get(storageKey);
    if (currentTombstones && currentTombstones.has(conv.id)) return;

    recordConversationMutation(storageKey, conv.id, 'insert', conv);
    queryClient.setQueryData<InfiniteData<ConversationsPageResponse>>(queryKey, (old) => {
      if (!old || !old.pages.length) {
        return {
          pages: [{ items: [conv], nextCursor: null, hasMore: false }],
          pageParams: [null]
        };
      }
      const exists = old.pages.some(p => p.items.some(c => c.id === conv.id));
      if (exists) {
        return {
          ...old,
          pages: old.pages.map(p => ({
            ...p,
            items: p.items.map(c => c.id === conv.id ? { ...c, ...conv } : c)
          }))
        };
      }
      const newPages = [...old.pages];
      newPages[0] = {
        ...newPages[0],
        items: [conv, ...newPages[0].items]
      };
      return {
        ...old,
        pages: newPages
      };
    });
    setMutationTick(t => t + 1);
  }, [storageKey, myGeneration, queryKey, refetch]);

  const updateConversationInCache = useCallback((id: string, updates: Partial<Conversation>) => {
    if (!isCallbackActive(storageKey, myGeneration)) return;
    const currentTombstones = tombstonesByKey.get(storageKey);
    if (currentTombstones && currentTombstones.has(id)) return;

    updateScopedConversationCache(queryKey, id, updates);
    setMutationTick(t => t + 1);
  }, [storageKey, myGeneration, queryKey]);

  const removeConversationFromCache = useCallback((id: string) => {
    if (!isCallbackActive(storageKey, myGeneration)) return;
    recordConversationMutation(storageKey, id, 'delete');
    queryClient.setQueryData<InfiniteData<ConversationsPageResponse>>(queryKey, (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map(p => ({
          ...p,
          items: p.items.filter(c => c.id !== id)
        }))
      };
    });
    setMutationTick(t => t + 1);
  }, [storageKey, myGeneration, queryKey]);

  const isError = Boolean(query.isError || syncError || effectiveAccessDenied);
  const error = syncError || (effectiveAccessDenied ? new Error(effectiveAccessDenied.message) : null) || query.error || null;

  return {
    conversations,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: Boolean(query.hasNextPage),
    isError,
    error,
    syncError,
    refetch,
    loadMore,
    addRealtimeConversation,
    updateConversationInCache,
    removeConversationFromCache
  };
}
